import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AuthModal.css';

type Mode = 'signin' | 'signup' | 'verify';

interface AuthModalProps {
  onClose: () => void;
  /** If set, shown as a prompt above the form */
  reason?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, reason }) => {
  const { signIn, signUp, confirmSignUp } = useAuth();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearError = () => setError('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await signIn(email, password);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Sign in failed. Please check your email and password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const { needsVerification } = await signUp(email, password);
      if (needsVerification) {
        setMode('verify');
      } else {
        await signIn(email, password);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await confirmSignUp(email, code);
      await signIn(email, password);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check the code and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="auth-header">
          <span className="auth-logo">🧠</span>
          <h2>Brain Break Cards</h2>
        </div>

        {reason && (
          <p className="auth-reason">{reason}</p>
        )}

        {mode === 'signin' && (
          <>
            <h3>Sign in</h3>
            <form onSubmit={handleSignIn} noValidate>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); clearError(); }}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); clearError(); }}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </label>
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <p className="auth-switch">
              Don't have an account?{' '}
              <button onClick={() => { setMode('signup'); clearError(); }}>Create one</button>
            </p>
          </>
        )}

        {mode === 'signup' && (
          <>
            <h3>Create an account</h3>
            <p className="auth-sub">Free · 20 card packs per month</p>
            <form onSubmit={handleSignUp} noValidate>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); clearError(); }}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); clearError(); }}
                  required
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                />
              </label>
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating account…' : 'Create account'}
              </button>
            </form>
            <p className="auth-switch">
              Already have an account?{' '}
              <button onClick={() => { setMode('signin'); clearError(); }}>Sign in</button>
            </p>
          </>
        )}

        {mode === 'verify' && (
          <>
            <h3>Check your email</h3>
            <p className="auth-sub">We sent a verification code to <strong>{email}</strong></p>
            <form onSubmit={handleVerify} noValidate>
              <label>
                Verification code
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={e => { setCode(e.target.value.replace(/\D/g, '')); clearError(); }}
                  required
                  placeholder="123456"
                  maxLength={6}
                />
              </label>
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying…' : 'Verify and sign in'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
